/**
 * Department Transfers hub — custodian outgoing / incoming lists.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import '../Approvals/DeviceApprovals.css';
import '../Transfers/DeviceTransfers.css';
import EmptyState from '../../features/dashboard/widgets/Shared/EmptyState';
import ErrorState from '../../features/dashboard/widgets/Shared/ErrorState';
import { DeviceIDBadge } from '../../shared/components/common/Badges';
import TransferStatusBadge from '../../features/transfers/components/TransferStatusBadge';
import TransferActor from '../../features/transfers/components/TransferActor';
import DepartmentTransferModal from '../../features/department-transfers/components/DepartmentTransferModal';
import TransferActionBanner from '../../features/transfers/components/TransferActionBanner';
import Pagination from '../../shared/components/common/Pagination';
import ListSearchBar from '../../shared/components/common/ListSearchBar';
import SortableTh from '../../shared/components/common/SortableTh';
import { getListPageSize, saveUiPreferencePatch } from '../../shared/utils/uiPreferences';
import { useServerTableSort } from '../../shared/utils/serverTableSort';
import '../../features/transfers/components/TransferRouteStack.css';

const EMPTY_ACTION_SUMMARY = { outgoing: 0, incoming: 0, total: 0 };
const OUTGOING_TABS = [
  { id: 'action_required', label: 'Action required' },
  { id: 'completed', label: 'Completed' },
  { id: 'rejected', label: 'Rejected / cancelled' },
  { id: 'all', label: 'All' },
];

const INCOMING_TABS = [
  { id: 'action_required', label: 'Action required' },
  { id: 'completed', label: 'Completed' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

const formatStackedDateTime = (dateString) => {
  if (!dateString) return { date: 'N/A', time: '' };
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

const readStoredNav = () => {
  const direction = sessionStorage.getItem('departmentTransferDirection');
  const statusTab = sessionStorage.getItem('departmentTransferStatusTab');
  if (direction) sessionStorage.removeItem('departmentTransferDirection');
  if (statusTab) sessionStorage.removeItem('departmentTransferStatusTab');
  return { direction, statusTab };
};

const DepartmentTransfersPage = ({ token, user, onActionCountsChange }) => {
  const storedNav = readStoredNav();
  const [direction, setDirection] = useState(storedNav.direction || 'outgoing');
  const [statusTab, setStatusTab] = useState(storedNav.statusTab || 'action_required');
  const [transfers, setTransfers] = useState([]);
  const [counts, setCounts] = useState({});
  const [actionSummary, setActionSummary] = useState(EMPTY_ACTION_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalTransfer, setModalTransfer] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: getListPageSize(),
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const { sorting, handleSortChange } = useServerTableSort('updated_at', 'desc');

  const tabs = direction === 'outgoing' ? OUTGOING_TABS : INCOMING_TABS;

  const fetchActionSummary = useCallback(async () => {
    if (!token) return EMPTY_ACTION_SUMMARY;
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENT_TRANSFERS.ACTION_SUMMARY, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const summary = {
        outgoing: response.data?.outgoing || 0,
        incoming: response.data?.incoming || 0,
        total: response.data?.total || 0,
      };
      setActionSummary(summary);
      return summary;
    } catch {
      setActionSummary(EMPTY_ACTION_SUMMARY);
      return EMPTY_ACTION_SUMMARY;
    }
  }, [token]);

  const fetchTransfers = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const params = {
        direction,
        status: statusTab,
        page: pagination.page,
        per_page: pagination.per_page,
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction,
      };
      if (search) {
        params.search = search;
      }
      const response = await axios.get(API_ENDPOINTS.DEPARTMENT_TRANSFERS.LIST, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setTransfers(response.data?.transfers || []);
      setCounts(response.data?.counts || {});
      if (response.data?.pagination) {
        setPagination({
          page: response.data.pagination.page,
          per_page: response.data.pagination.per_page,
          total: response.data.pagination.total || 0,
          total_pages: response.data.pagination.total_pages || 0,
          has_next: response.data.pagination.has_next || false,
          has_prev: response.data.pagination.has_prev || false,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load department transfers');
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [token, direction, statusTab, pagination.page, pagination.per_page, search, sorting.sort_by, sorting.sort_direction]);

  const refreshHub = useCallback(async () => {
    await Promise.all([fetchTransfers(), fetchActionSummary()]);
    if (typeof onActionCountsChange === 'function') {
      onActionCountsChange();
    }
  }, [fetchTransfers, fetchActionSummary, onActionCountsChange]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  useEffect(() => {
    fetchActionSummary();
  }, [fetchActionSummary]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleDirectionChange = (next) => {
    setDirection(next);
    setStatusTab('action_required');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusTabChange = (tabId) => {
    setStatusTab(tabId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!value) {
      setSearch('');
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  };

  const handleSortChangeWithReset = (column) => {
    handleSortChange(column);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getRowAction = (t) => {
    if (t.status !== 'pending_destination_approval') {
      return { label: 'View', mode: 'view', variant: 'secondary' };
    }
    if (direction === 'incoming' && t.destination_custodian_employee_id === user?.employee_id) {
      return { label: 'Review', mode: 'accept', variant: 'primary' };
    }
    if (direction === 'outgoing' && t.requested_by_employee_id === user?.employee_id) {
      return { label: 'Cancel', mode: 'cancel', variant: 'secondary' };
    }
    return { label: 'View', mode: 'view', variant: 'secondary' };
  };

  const openTransfer = (t, mode) => {
    setModalTransfer(t);
    setModalMode(mode);
  };

  if (loading && transfers.length === 0 && !error) {
    return (
      <div className="device-approval-dashboard device-transfers-page">
        <div className="approval-header">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            🔁 Department Transfers
          </h1>
        </div>
        <p>Loading…</p>
      </div>
    );
  }

  if (error && transfers.length === 0) {
    return <ErrorState error={error} onRetry={refreshHub} />;
  }

  return (
    <div className="device-approval-dashboard device-transfers-page">
      <div className="approval-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            🔁 Department Transfers
          </h1>
          <button
            type="button"
            onClick={refreshHub}
            className="btn-refresh"
            title="Refresh"
            style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer' }}
          >
            🔄
          </button>
        </div>
        <p>Same-location department handoffs between assigned custodians</p>
      </div>

      <TransferActionBanner summary={actionSummary} onSelectDirection={handleDirectionChange} />

      <div className="transfer-direction-filter">
        <span className="transfer-direction-filter__label">Queue</span>
        <div className="transfer-direction-tabs" role="tablist" aria-label="Transfer queue">
          <button
            type="button"
            className={`tab ${direction === 'outgoing' ? 'active' : ''}`}
            onClick={() => handleDirectionChange('outgoing')}
          >
            Outgoing
            <span className="direction-tab-count">{actionSummary.outgoing ?? 0}</span>
          </button>
          <button
            type="button"
            className={`tab ${direction === 'incoming' ? 'active' : ''}`}
            onClick={() => handleDirectionChange('incoming')}
          >
            Incoming
            <span className="direction-tab-count">{actionSummary.incoming ?? 0}</span>
          </button>
        </div>
      </div>

      <div className="transfer-status-tabs" role="tablist" aria-label="Transfer status">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab ${statusTab === tab.id ? 'active' : ''}`}
            onClick={() => handleStatusTabChange(tab.id)}
            title={tab.label}
          >
            <span className="tab-label">{tab.label}</span>
            <span className="tab-count">{counts[tab.id] ?? 0}</span>
          </button>
        ))}
      </div>

      <ListSearchBar
        value={searchInput}
        onChange={handleSearchChange}
        onClear={handleClearSearch}
        placeholder="Search device ID, name, departments, requester, or destination custodian…"
      />

      <div className="approval-table-container">
        {transfers.length === 0 ? (
          <EmptyState
            section="Department Transfers"
            message={search ? 'No transfers match your search in this tab.' : 'No transfers in this tab.'}
            icon="🔁"
          />
        ) : (
          <table className="transfers-table">
            <thead>
              <tr>
                <SortableTh column="device_name" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Device
                </SortableTh>
                <SortableTh column="from_department" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Route
                </SortableTh>
                <SortableTh column="status" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Status
                </SortableTh>
                <SortableTh column="requested_by" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Requested
                </SortableTh>
                <SortableTh column="destination_custodian" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Recipient
                </SortableTh>
                <SortableTh column="updated_at" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                  Updated
                </SortableTh>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => {
                const action = getRowAction(t);
                const updated = formatStackedDateTime(t.updated_at);
                return (
                  <tr key={t.transfer_id}>
                    <td>
                      <div className="transfer-device-cell">
                        <span className="transfer-device-name">{t.device_name || 'Device'}</span>
                        <DeviceIDBadge deviceId={t.device_identifier || 'N/A'} size="small" />
                      </div>
                    </td>
                    <td className="transfer-route-cell">
                      <span className="transfer-route-text">
                        {t.from_department_name}
                        <span className="transfer-route-arrow" aria-hidden="true">
                          →
                        </span>
                        {t.to_department_name}
                      </span>
                    </td>
                    <td>
                      <TransferStatusBadge status={t.status} size="small" />
                    </td>
                    <td className="stacked-info transfer-actor-cell">
                      <TransferActor
                        name={t.requested_by_name || t.requested_by_employee_id}
                        role={t.requested_by_role}
                        layout="stacked"
                      />
                    </td>
                    <td className="stacked-info transfer-actor-cell">
                      <TransferActor
                        name={t.destination_custodian_name || t.destination_custodian_employee_id}
                        role={t.destination_custodian_role}
                        layout="stacked"
                      />
                    </td>
                    <td className="stacked-info transfer-date-cell">
                      {updated.date === 'N/A' ? (
                        <span className="na-text">N/A</span>
                      ) : (
                        <>
                          <div className="stack-item transfer-date-primary">{updated.date}</div>
                          <div className="stack-item transfer-date-secondary">{updated.time}</div>
                        </>
                      )}
                    </td>
                    <td className="transfer-action-cell">
                      <button
                        type="button"
                        className={`btn-action ${action.variant === 'primary' ? 'btn-edit' : 'btn-view'}`}
                        onClick={() => openTransfer(t, action.mode)}
                      >
                        {action.label}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
          perPage={pagination.per_page}
          onPerPageChange={(newPerPage) => {
            saveUiPreferencePatch({ list_page_size: newPerPage });
            setPagination((prev) => ({ ...prev, page: 1, per_page: newPerPage }));
          }}
          totalItems={pagination.total}
          itemLabel="transfers"
        />
      )}

      {modalTransfer && (
        <DepartmentTransferModal
          token={token}
          user={user}
          mode={modalMode}
          transfer={modalTransfer}
          onClose={() => {
            setModalTransfer(null);
            setModalMode('view');
          }}
          onSuccess={refreshHub}
        />
      )}
    </div>
  );
};

export default DepartmentTransfersPage;
